import { BASE_URL, PUBLIC_SITE_URL } from "./constants";

function isLocalDevelopmentOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);
    const h = hostname.toLowerCase();
    return (
      h === "localhost" ||
      h === "127.0.0.1" ||
      h === "[::1]" ||
      h.endsWith(".local")
    );
  } catch {
    return false;
  }
}

/**
 * Origin for ticket/product/share links.
 * On localhost, prefers VITE_PUBLIC_SITE_URL / VITE_BASE_URL (public site, e.g. :3000)
 * so admin on another port still shares the correct frontend URL.
 * In production, prefers VITE_PUBLIC_SITE_URL, then VITE_BASE_URL, then the tab origin.
 */
export function publicSiteOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    const tab = window.location.origin.replace(/\/$/, "");
    if (isLocalDevelopmentOrigin(tab)) {
      if (PUBLIC_SITE_URL) return PUBLIC_SITE_URL.replace(/\/$/, "");
      if (typeof BASE_URL === "string" && BASE_URL.trim())
        return BASE_URL.replace(/\/$/, "");
      return tab;
    }
  }
  if (PUBLIC_SITE_URL) return PUBLIC_SITE_URL.replace(/\/$/, "");
  if (typeof BASE_URL === "string" && BASE_URL.trim())
    return BASE_URL.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return "";
}

/** Human-readable public product path — prefers stable show slug. */
export function publicProductPagePath(showSlug: string): string {
  if (!showSlug || typeof showSlug !== "string") return "";
  return `/products/${showSlug}`;
}

export function absolutePublicProductUrl(showSlug: string): string {
  if (!showSlug) return "";
  return `${publicSiteOrigin()}${publicProductPagePath(showSlug)}`;
}

/** Human-readable public festival path — prefers stable festival slug. */
export function publicFestivalPagePath(festivalSlug: string): string {
  if (!festivalSlug || typeof festivalSlug !== "string") return "";
  return `/festivals/${festivalSlug}`;
}

export function absolutePublicFestivalUrl(festivalSlug: string): string {
  if (!festivalSlug) return "";
  return `${publicSiteOrigin()}${publicFestivalPagePath(festivalSlug)}`;
}

function nonEmptyTrimmed(s: string | null | undefined): boolean {
  return typeof s === "string" && s.trim().length > 0;
}

/**
 * True for http(s) URLs that are not historic QR code / image assets stored in ticket_url.
 */
export function isUsableExternalTicketUrl(
  raw: string | null | undefined,
): boolean {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) return false;
  const low = trimmed.toLowerCase();
  const isQrOrImageAsset =
    /\.(png|jpe?g|gif|webp)(\?|$)/i.test(low) ||
    low.includes("qr_codes/") ||
    low.includes("cuo-order-qr-codes");
  return !isQrOrImageAsset;
}

function isComedyShopTicketUrl(raw: string | null | undefined): boolean {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed) return false;
  try {
    const { hostname } = new URL(trimmed);
    return hostname.toLowerCase() === "shop.comedyuo.com";
  } catch {
    return false;
  }
}

/** Third-party checkout (not ComedyUO / shop.comedyuo.com). */
export function isOffsitePurchaseUrl(raw: string | null | undefined): boolean {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) return false;
  try {
    const host = new URL(trimmed).hostname.toLowerCase();
    if (host === "shop.comedyuo.com") return false;
    if (host === "comedyuo.com" || host.endsWith(".comedyuo.com")) return false;
    return true;
  } catch {
    return false;
  }
}

export type PublicShowPurchaseLinkInput = {
  slug?: string | null;
  parent_slug?: string | null;
  id?: string | null;
  title?: string | null;
  variant_name?: string | null;
  stripe_product_id?: string | null;
  ticket_url?: string | null;
};

/** Partner checkout for KidSuper Presents: Funny Business (Oct 27 2026). */
export const KIDSUPER_FUNNY_BUSINESS_TICKET_URL =
  "https://www.funnybusinesses.com/shows/funny-business-live-at-kidsuper-studios-11-2026-10-27?dub_id=eNaCK7L7f0aZXZte";

export function isKidSuperFunnyBusinessShow(input: {
  slug?: string | null;
  title?: string | null;
  variant_name?: string | null;
}): boolean {
  const slug = (input.slug || "").toLowerCase();
  const title = `${input.title || ""} ${input.variant_name || ""}`.toLowerCase();
  if (
    slug.includes("kidsuper") ||
    slug.includes("funny-business") ||
    slug.includes("funnybusiness")
  ) {
    return true;
  }
  return title.includes("kidsuper") || title.includes("funny business");
}

function productPageSegment(input: PublicShowPurchaseLinkInput): string {
  // Double-header children all render the header's page, so share one link.
  if (nonEmptyTrimmed(input.parent_slug)) return String(input.parent_slug).trim();
  if (nonEmptyTrimmed(input.slug)) return String(input.slug).trim();
  if (input.id) return String(input.id).trim();
  return "";
}

/**
 * Public “Get tickets” / share href:
 * - KidSuper Funny Business → funnybusinesses.com (even if Stripe product exists)
 * - Off-site ticket_url (partner checkout) wins over on-site Stripe pages
 * - shop.comedyuo.com ticket_url next
 * - Stripe-backed show → on-site product page
 * - Else usable ticket_url, else on-site product page by slug/id
 */
export function publicShowPurchaseUrl(
  input: PublicShowPurchaseLinkInput,
): string {
  if (isKidSuperFunnyBusinessShow(input)) {
    if (
      isUsableExternalTicketUrl(input.ticket_url) &&
      isOffsitePurchaseUrl(input.ticket_url)
    ) {
      return String(input.ticket_url).trim();
    }
    return KIDSUPER_FUNNY_BUSINESS_TICKET_URL;
  }
  if (
    isUsableExternalTicketUrl(input.ticket_url) &&
    isOffsitePurchaseUrl(input.ticket_url)
  ) {
    return String(input.ticket_url).trim();
  }
  if (
    isUsableExternalTicketUrl(input.ticket_url) &&
    isComedyShopTicketUrl(input.ticket_url)
  ) {
    return String(input.ticket_url).trim();
  }
  const segment = productPageSegment(input);
  if (nonEmptyTrimmed(input.stripe_product_id) && segment) {
    return absolutePublicProductUrl(segment);
  }
  if (isUsableExternalTicketUrl(input.ticket_url)) {
    return String(input.ticket_url).trim();
  }
  if (segment) {
    return absolutePublicProductUrl(segment);
  }
  return "";
}

/** Path segment is a show UUID (legacy links remain valid). */
export function isUuidRouteParam(param: string | undefined): boolean {
  if (!param) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    param,
  );
}

/** @deprecated Use isUuidRouteParam */
export const isShowIdRouteParam = isUuidRouteParam;
